import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Save, Plus, Trash2, GripVertical, ChevronDown, ChevronUp, ChevronRight, Home,
  Pencil, Copy, FileText, Users, DollarSign, Send, Camera, PenTool,
  Hammer, CheckCircle2, FolderOpen, ClipboardCheck, Star, Lock, Unlock,
  ExternalLink, Link as LinkIcon, Lightbulb, Target, Info, BookOpen,
  Settings, Layers, AlertTriangle, Check, X, MoreHorizontal, RotateCcw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Alert,
  AlertDescription,
} from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { BrandLockup } from '@/components/brand/Brand';
import { FIRST_PHASE_NUMBER, renumber } from '@/utils/phaseNumbering';

const API = process.env.REACT_APP_BACKEND_URL;

// Action type options
const ACTION_TYPES = [
  { value: 'file', label: 'File Management', icon: FolderOpen, color: 'text-indigo-500' },
  { value: 'document', label: 'Document', icon: FileText, color: 'text-blue-500' },
  { value: 'email', label: 'Email', icon: Send, color: 'text-green-500' },
  { value: 'survey', label: 'Survey', icon: ClipboardCheck, color: 'text-purple-500' },
  { value: 'invoice', label: 'Invoice', icon: DollarSign, color: 'text-emerald-500' },
  { value: 'meeting', label: 'Meeting', icon: Users, color: 'text-orange-500' },
  { value: 'site_visit', label: 'Site Visit', icon: Camera, color: 'text-cyan-500' },
  { value: 'design', label: 'Design', icon: PenTool, color: 'text-pink-500' },
  { value: 'contractor', label: 'Contractor', icon: Hammer, color: 'text-amber-500' },
  { value: 'approval', label: 'Approval', icon: CheckCircle2, color: 'text-green-600' },
];

// KEY-MADE page links
const KEYMADE_LINKS = [
  { value: '', label: 'None' },
  { value: '/projects', label: 'Projects' },
  { value: '/documents', label: 'Document Library' },
  { value: '/documents/contractor-agreement/new', label: 'Contractor Agreement' },
  { value: '/surveys', label: 'Surveys' },
  { value: '/contacts', label: 'Contacts' },
  { value: '/contractors', label: 'Contractors' },
  { value: '/estimating', label: 'Estimating' },
  { value: '/scope-statements', label: 'Scope Statements' },
  { value: '/floorplans', label: 'Floor Plans' },
  { value: '/moodboards', label: 'Mood Boards' },
  { value: '/portals', label: 'Client Portals' },
  { value: '/calendar', label: 'Calendar' },
];

// Sortable Step Item Component
function SortableStepItem({ step, phaseId, index, totalSteps, onEdit, onDelete, onDuplicate, onMoveToPhase, onMoveUp, onMoveDown, otherPhases, darkMode, allSteps }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: step.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const actionType = ACTION_TYPES.find(a => a.value === step.action_type);
  const ActionIcon = actionType?.icon || FileText;
  const isFirst = index === 0;
  const isLast = index === totalSteps - 1;

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`step-row-${step.id}`}
      data-step-index={index}
      data-step-name={step.name}
      className={`flex items-start gap-3 p-3 rounded-lg border ${
        isDragging 
          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
          : darkMode 
          ? 'border-[#2A2A2A] bg-[#1A1A1A]/50 hover:bg-gray-800' 
          : 'border-gray-200 bg-white hover:bg-gray-50'
      } transition-colors`}
    >
      {/* Drag Handle — only this element receives dnd-kit listeners so
          clicks on the Move / Edit / Delete buttons never start a drag. */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder step"
        data-testid={`step-drag-handle-${step.id}`}
        className="flex-shrink-0 p-1 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 touch-none"
      >
        <GripVertical className="w-4 h-4" />
      </button>

      {/* Move Up / Move Down arrows — always visible, click-driven
          reorder that works whether or not drag is available. */}
      <div className="flex flex-col gap-0.5 flex-shrink-0">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-5 w-5"
          disabled={isFirst}
          onClick={(e) => { e.stopPropagation(); onMoveUp && onMoveUp(index); }}
          aria-label="Move step up"
          title="Move up"
          data-testid={`step-move-up-${step.id}`}
        >
          <ChevronUp className="w-3.5 h-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-5 w-5"
          disabled={isLast}
          onClick={(e) => { e.stopPropagation(); onMoveDown && onMoveDown(index); }}
          aria-label="Move step down"
          title="Move down"
          data-testid={`step-move-down-${step.id}`}
        >
          <ChevronDown className="w-3.5 h-3.5" />
        </Button>
      </div>

      {/* Step Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={`font-medium text-sm ${darkMode ? 'text-white' : ''}`}>
            {step.name}
          </span>
          {step.is_critical && (
            <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
          )}
          {/* DP41 — the client-reply flag on the collapsed row: a phase is
              scannable without opening nine dialogs. */}
          {step.requires_client_reply && (
            <Badge
              variant="outline"
              className="text-[9px] h-4 px-1"
              data-testid={`step-client-reply-badge-${step.id}`}
            >
              Client reply
            </Badge>
          )}
          {step.is_optional && (
            <Badge variant="outline" className="text-[9px] h-4 px-1">Optional</Badge>
          )}
          {actionType && (
            <Badge variant="outline" className={`text-[9px] h-4 ${actionType.color}`}>
              <ActionIcon className="w-2.5 h-2.5 mr-0.5" />
              {actionType.label}
            </Badge>
          )}
        </div>
        <p className={`text-xs truncate ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
          {step.description || step.definition?.substring(0, 100) || 'No description'}
        </p>
        {step.dependencies?.length > 0 && (
          <div className="flex items-center gap-1 mt-1 text-[10px] text-amber-600">
            <Lock className="w-3 h-3" />
            <span>Requires: {step.dependencies.join(', ')}</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" data-testid={`step-actions-menu-${step.id}`}>
            <MoreHorizontal className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
          <DropdownMenuItem onClick={() => onEdit(step)} data-testid={`step-edit-${step.id}`}>
            <Pencil className="w-4 h-4 mr-2" />
            Edit Step
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onDuplicate(step)} data-testid={`step-duplicate-${step.id}`}>
            <Copy className="w-4 h-4 mr-2" />
            Duplicate
          </DropdownMenuItem>
          {otherPhases && otherPhases.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <div className={`px-2 py-1 text-[10px] uppercase tracking-wide ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Move to phase</div>
              {otherPhases.map(p => (
                <DropdownMenuItem
                  key={p.id}
                  onClick={() => onMoveToPhase && onMoveToPhase(step, phaseId, p.id)}
                  data-testid={`move-step-to-phase-${p.id}`}
                >
                  <ChevronRight className="w-4 h-4 mr-2" />
                  {p.name || `Phase ${p.phase_number ?? '?'}`}
                </DropdownMenuItem>
              ))}
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onDelete(step)} className="text-red-600" data-testid={`step-delete-${step.id}`}>
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// Sortable Phase Component
function SortablePhase({ phase, onUpdate, onDelete, onStepsReorder, onMoveStep, darkMode, allPhases }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [editingStep, setEditingStep] = useState(null);
  const [showStepDialog, setShowStepDialog] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: phase.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (active.id !== over?.id) {
      const oldIndex = phase.steps.findIndex(s => s.id === active.id);
      const newIndex = phase.steps.findIndex(s => s.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newSteps = arrayMove(phase.steps, oldIndex, newIndex);
        onStepsReorder(phase.id, newSteps);
      }
    }
  };

  const handleEditStep = (step) => {
    setEditingStep({ ...step });
    setShowStepDialog(true);
  };

  const handleAddStep = () => {
    setEditingStep({
      id: `step-${Date.now()}`,
      name: '',
      description: '',
      definition: '',
      why_it_matters: '',
      deliverable_description: '',
      tips: [],
      action_type: '',
      keymade_link: '',
      external_link: '',
      external_link_label: '',
      dependencies: [],
      is_critical: false,
      is_optional: false,
      requires_client_reply: false,   // DP41 — default off, set by the company
    });
    setShowStepDialog(true);
  };

  const handleSaveStep = () => {
    if (!editingStep.name) {
      toast.error('Step name is required');
      return;
    }

    const existingIndex = phase.steps.findIndex(s => s.id === editingStep.id);
    let newSteps;
    
    if (existingIndex >= 0) {
      newSteps = [...phase.steps];
      newSteps[existingIndex] = editingStep;
    } else {
      newSteps = [...phase.steps, editingStep];
    }

    onUpdate(phase.id, { steps: newSteps });
    setShowStepDialog(false);
    setEditingStep(null);
    toast.success(existingIndex >= 0 ? 'Step updated' : 'Step added');
  };

  const handleDeleteStep = (step) => {
    const newSteps = phase.steps.filter(s => s.id !== step.id);
    onUpdate(phase.id, { steps: newSteps });
    toast.success('Step deleted');
  };

  const handleDuplicateStep = (step) => {
    const newStep = {
      ...step,
      id: `step-${Date.now()}`,
      name: `${step.name} (Copy)`,
    };
    onUpdate(phase.id, { steps: [...phase.steps, newStep] });
    toast.success('Step duplicated');
  };

  // Click-driven reorder (buttons). Uses the same onStepsReorder
  // pipeline as drag-and-drop so the state update path is identical
  // and the "Unsaved Changes" flag/save behaviour matches.
  const handleMoveStepUp = (idx) => {
    if (idx <= 0 || !phase.steps || idx >= phase.steps.length) return;
    const newSteps = arrayMove(phase.steps, idx, idx - 1);
    onStepsReorder(phase.id, newSteps);
  };

  const handleMoveStepDown = (idx) => {
    if (!phase.steps || idx < 0 || idx >= phase.steps.length - 1) return;
    const newSteps = arrayMove(phase.steps, idx, idx + 1);
    onStepsReorder(phase.id, newSteps);
  };

  // Get all step names for dependency selection
  const allStepNames = allPhases.flatMap(p => p.steps.map(s => s.name));

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-lg border ${
        isDragging 
          ? 'border-blue-500' 
          : darkMode 
          ? 'border-[#2A2A2A] bg-[#1A1A1A]/30' 
          : 'border-gray-200 bg-white'
      }`}
    >
      {/* Phase Header */}
      <div className={`flex items-center gap-3 p-4 ${darkMode ? 'border-[#2A2A2A]' : 'border-gray-200'} ${isExpanded ? 'border-b' : ''}`}>
        <button
          {...attributes}
          {...listeners}
          className="flex-shrink-0 p-1 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
        >
          <GripVertical className="w-5 h-5" />
        </button>

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex-shrink-0"
        >
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-400" />
          )}
        </button>

        <div className="flex-1 min-w-0">
          <Input
            value={phase.name}
            onChange={(e) => onUpdate(phase.id, { name: e.target.value })}
            className={`font-semibold text-lg border-0 p-0 h-auto focus-visible:ring-0 ${darkMode ? 'bg-transparent text-white' : 'bg-transparent'}`}
            placeholder="Phase Name"
          />
          <Input
            value={phase.description || ''}
            onChange={(e) => onUpdate(phase.id, { description: e.target.value })}
            className={`text-sm border-0 p-0 h-auto focus-visible:ring-0 mt-1 ${darkMode ? 'bg-transparent text-gray-400' : 'bg-transparent text-muted-foreground'}`}
            placeholder="Phase description..."
          />
        </div>

        <Badge variant="outline" className="flex-shrink-0">
          {phase.steps?.length || 0} steps
        </Badge>

        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDelete(phase)}
          className="text-red-500 hover:text-red-700 hover:bg-red-50"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      {/* Steps List */}
      {isExpanded && (
        <div className="p-4 space-y-2">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={phase.steps?.map(s => s.id) || []}
              strategy={verticalListSortingStrategy}
            >
              {phase.steps?.map((step, stepIdx) => (
                <SortableStepItem
                  key={step.id}
                  step={step}
                  phaseId={phase.id}
                  index={stepIdx}
                  totalSteps={phase.steps.length}
                  onEdit={handleEditStep}
                  onDelete={handleDeleteStep}
                  onDuplicate={handleDuplicateStep}
                  onMoveToPhase={onMoveStep}
                  onMoveUp={handleMoveStepUp}
                  onMoveDown={handleMoveStepDown}
                  otherPhases={(allPhases || []).filter(p => p.id !== phase.id)}
                  darkMode={darkMode}
                  allSteps={allStepNames}
                />
              ))}
            </SortableContext>
          </DndContext>

          {(!phase.steps || phase.steps.length === 0) && (
            <div className={`text-center py-8 rounded-lg border-2 border-dashed ${darkMode ? 'border-[#2A2A2A]' : 'border-gray-200'}`}>
              <p className="text-sm text-muted-foreground mb-2">No steps yet</p>
            </div>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={handleAddStep}
            className="w-full mt-2"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Step
          </Button>
        </div>
      )}

      {/* Step Edit Dialog */}
      <Dialog open={showStepDialog} onOpenChange={setShowStepDialog}>
        <DialogContent className={`max-w-2xl max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-gray-900 border-gray-800' : ''}`}>
          <DialogHeader>
            <DialogTitle className={darkMode ? 'text-white' : ''}>
              {editingStep?.id?.startsWith('step-') ? 'Add Step' : 'Edit Step'}
            </DialogTitle>
            <DialogDescription>
              Configure the step details, guidance, and links
            </DialogDescription>
          </DialogHeader>

          {editingStep && (
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="space-y-4">
                <h4 className={`font-medium text-sm ${darkMode ? 'text-white' : ''}`}>Basic Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Step Name *</Label>
                    <Input
                      value={editingStep.name}
                      onChange={(e) => setEditingStep(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Send Welcome Email"
                      className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Short Description</Label>
                    <Input
                      value={editingStep.description || ''}
                      onChange={(e) => setEditingStep(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Brief description shown in list"
                      className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Step Guide Content */}
              <div className="space-y-4">
                <h4 className={`font-medium text-sm flex items-center gap-2 ${darkMode ? 'text-white' : ''}`}>
                  <BookOpen className="w-4 h-4 text-blue-500" />
                  Step Guide Content
                </h4>
                
                <div>
                  <Label>What to Do (Definition)</Label>
                  <Textarea
                    value={editingStep.definition || ''}
                    onChange={(e) => setEditingStep(prev => ({ ...prev, definition: e.target.value }))}
                    placeholder="Detailed instructions on how to complete this step..."
                    rows={3}
                    className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}
                  />
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    <Lightbulb className="w-3 h-3 text-amber-500" />
                    Why It Matters
                  </Label>
                  <Textarea
                    value={editingStep.why_it_matters || ''}
                    onChange={(e) => setEditingStep(prev => ({ ...prev, why_it_matters: e.target.value }))}
                    placeholder="Explain the business context and importance..."
                    rows={2}
                    className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}
                  />
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    <Target className="w-3 h-3 text-green-500" />
                    Deliverable
                  </Label>
                  <Input
                    value={editingStep.deliverable_description || ''}
                    onChange={(e) => setEditingStep(prev => ({ ...prev, deliverable_description: e.target.value }))}
                    placeholder="e.g., Signed contract saved to project file"
                    className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}
                  />
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    <Lightbulb className="w-3 h-3 text-amber-500" />
                    Assistance Tips (one per line)
                  </Label>
                  <Textarea
                    value={editingStep.tips?.join('\n') || ''}
                    onChange={(e) => setEditingStep(prev => ({ 
                      ...prev, 
                      tips: e.target.value.split('\n').filter(t => t.trim()) 
                    }))}
                    placeholder="Best practice advice...&#10;One tip per line...&#10;These appear in 'Get Assistance' panel"
                    rows={4}
                    className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}
                  />
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    These tips appear when users click &quot;Get Assistance&quot; in the Project Journey
                  </p>
                </div>
              </div>

              <Separator />

              {/* Action Type & Links */}
              <div className="space-y-4">
                <h4 className={`font-medium text-sm flex items-center gap-2 ${darkMode ? 'text-white' : ''}`}>
                  <LinkIcon className="w-4 h-4 text-indigo-500" />
                  Action Type & Links
                </h4>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Action Type</Label>
                    <Select
                      value={editingStep.action_type || 'none'}
                      onValueChange={(value) => setEditingStep(prev => ({ ...prev, action_type: value === 'none' ? '' : value }))}
                    >
                      <SelectTrigger className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                        <SelectValue placeholder="Select type..." />
                      </SelectTrigger>
                      <SelectContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                        {/* Radix Select v2 throws on value="" — use a 'none' sentinel */}
                        <SelectItem value="none">None</SelectItem>
                        {ACTION_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            <div className="flex items-center gap-2">
                              <type.icon className={`w-4 h-4 ${type.color}`} />
                              {type.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>KEY-MADE Link</Label>
                    <Select
                      value={editingStep.keymade_link || 'none'}
                      onValueChange={(value) => setEditingStep(prev => ({ ...prev, keymade_link: value === 'none' ? '' : value }))}
                    >
                      <SelectTrigger className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                        <SelectValue placeholder="Link to page..." />
                      </SelectTrigger>
                      <SelectContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                        {KEYMADE_LINKS.map(link => (
                          <SelectItem key={link.value || 'none'} value={link.value || 'none'}>
                            {link.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>External Link URL</Label>
                    <Input
                      value={editingStep.external_link || ''}
                      onChange={(e) => setEditingStep(prev => ({ ...prev, external_link: e.target.value }))}
                      placeholder="https://..."
                      className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}
                    />
                  </div>

                  <div>
                    <Label>External Link Label</Label>
                    <Input
                      value={editingStep.external_link_label || ''}
                      onChange={(e) => setEditingStep(prev => ({ ...prev, external_link_label: e.target.value }))}
                      placeholder="e.g., Open Xero"
                      className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Dependencies & Flags */}
              <div className="space-y-4">
                <h4 className={`font-medium text-sm flex items-center gap-2 ${darkMode ? 'text-white' : ''}`}>
                  <Lock className="w-4 h-4 text-amber-500" />
                  Dependencies & Flags
                </h4>

                <div>
                  <Label>Prerequisites (step names that must be completed first)</Label>
                  <Textarea
                    value={editingStep.dependencies?.join('\n') || ''}
                    onChange={(e) => setEditingStep(prev => ({ 
                      ...prev, 
                      dependencies: e.target.value.split('\n').filter(d => d.trim()) 
                    }))}
                    placeholder="Contract/Proposal SIGNED&#10;Deposit Received"
                    rows={2}
                    className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    One step name per line. Step will be locked until all prerequisites are complete.
                  </p>
                </div>

                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={editingStep.is_critical || false}
                      onCheckedChange={(checked) => setEditingStep(prev => ({ ...prev, is_critical: checked }))}
                    />
                    <Label className="flex items-center gap-1 cursor-pointer">
                      <Star className="w-3 h-3 text-amber-500" />
                      Critical Step
                    </Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      checked={editingStep.is_optional || false}
                      onCheckedChange={(checked) => setEditingStep(prev => ({ ...prev, is_optional: checked }))}
                    />
                    <Label className="cursor-pointer">Optional Step</Label>
                  </div>

                  {/* DP41 — REQUIRES CLIENT REPLY. A fact about the step, set
                      once here by the company: once Chad has done his part, is
                      this waiting on the client? It draws the band's lower row. */}
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={editingStep.requires_client_reply || false}
                      onCheckedChange={(checked) => setEditingStep(prev => ({ ...prev, requires_client_reply: checked }))}
                      data-testid="step-client-reply-toggle"
                    />
                    <Label className="cursor-pointer">Requires Client Reply</Label>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStepDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveStep} className="bg-[#70170F] hover:bg-[#70170F]/90 text-white">
              <Save className="w-4 h-4 mr-2" />
              Save Step
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function PhaseTemplateEditor() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDesc, setNewTemplateDesc] = useState('');

  // Dark mode
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('keyMadeDarkMode');
    return saved === 'true';
  });

  useEffect(() => {
    const handleDarkModeChange = () => {
      const saved = localStorage.getItem('keyMadeDarkMode');
      setDarkMode(saved === 'true');
    };
    window.addEventListener('storage', handleDarkModeChange);
    const interval = setInterval(handleDarkModeChange, 2000);
    return () => {
      window.removeEventListener('storage', handleDarkModeChange);
      clearInterval(interval);
    };
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/project-templates`);
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplate = async (templateId) => {
    try {
      const response = await axios.get(`${API}/api/project-templates/${templateId}`);
      const template = response.data;
      setSelectedTemplate(template);
      setPhases(template.phases || []);
      setHasChanges(false);
    } catch (error) {
      console.error('Error loading template:', error);
      toast.error('Failed to load template');
    }
  };

  const handlePhaseUpdate = (phaseId, updates) => {
    setPhases(prev => prev.map(p => 
      p.id === phaseId ? { ...p, ...updates } : p
    ));
    setHasChanges(true);
  };

  const handlePhaseDelete = (phase) => {
    setPhases(prev => prev.filter(p => p.id !== phase.id));
    setHasChanges(true);
    toast.success('Phase deleted');
  };

  const handleStepsReorder = (phaseId, newSteps) => {
    setPhases(prev => prev.map(p => 
      p.id === phaseId ? { ...p, steps: newSteps } : p
    ));
    setHasChanges(true);
  };

  // 2026-02-17 — Move a step from one phase to another. Appends to
  // the end of the target phase; preserves every other step field.
  const handleMoveStep = (step, fromPhaseId, toPhaseId) => {
    if (!step || !fromPhaseId || !toPhaseId || fromPhaseId === toPhaseId) return;
    setPhases(prev => prev.map(p => {
      if (p.id === fromPhaseId) {
        return { ...p, steps: (p.steps || []).filter(s => s.id !== step.id) };
      }
      if (p.id === toPhaseId) {
        return { ...p, steps: [...(p.steps || []), step] };
      }
      return p;
    }));
    setHasChanges(true);
    toast.success(`Moved "${step.name}" to another phase`);
  };

  const handlePhasesReorder = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setPhases(prev => {
        const oldIndex = prev.findIndex(p => p.id === active.id);
        const newIndex = prev.findIndex(p => p.id === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
      setHasChanges(true);
    }
  };

  const handleAddPhase = () => {
    const newPhase = {
      id: `phase-${Date.now()}`,
      phase_number: phases.length + FIRST_PHASE_NUMBER,
      name: 'New Phase',
      description: '',
      steps: [],
    };
    setPhases(prev => [...prev, newPhase]);
    setHasChanges(true);
  };

  const handleSaveTemplate = async () => {
    if (!selectedTemplate) return;
    if (selectedTemplate.source === 'platform_default') {
      toast.error('This is a platform default — use "Customize" to create your editable copy first');
      return;
    }

    try {
      setSaving(true);
      
      // DP103 ruling 1b — the one renumbering. This said `phase_number: idx`
      // while AdminSettings' editor said `idx + 1`, so whichever saved last
      // decided a template's base.
      const updatedPhases = renumber(phases);

      await axios.put(`${API}/api/project-templates/${selectedTemplate.id}`, {
        phases: updatedPhases,
      });

      setHasChanges(false);
      toast.success('Template saved successfully!');
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  // Copy-on-write: customizing a platform default creates this company's
  // own editable copy; the shared default stays untouched.
  const handleCustomizeTemplate = async () => {
    if (!selectedTemplate) return;
    try {
      setSaving(true);
      const updatedPhases = renumber(phases);
      await axios.put(`${API}/api/project-templates/${selectedTemplate.id}`, {
        phases: updatedPhases,
      });
      toast.success("Created your company's own copy — template is now editable");
      await fetchTemplates();
      await loadTemplate(selectedTemplate.id);
    } catch (error) {
      console.error('Error customizing template:', error);
      toast.error('Failed to customize template');
    } finally {
      setSaving(false);
    }
  };

  // Revert a customized built-in back to the platform default (deletes
  // only this company's override doc).
  const handleResetTemplate = async (templateId) => {
    if (!window.confirm('Reset this template to the platform default? Your customizations will be removed.')) return;
    try {
      await axios.delete(`${API}/api/project-templates/${templateId}`);
      toast.success('Reverted to platform default');
      await fetchTemplates();
      if (selectedTemplate?.id === templateId) {
        await loadTemplate(templateId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset');
    }
  };

  const handleCreateTemplate = async () => {
    if (!newTemplateName.trim()) {
      toast.error('Template name is required');
      return;
    }
    try {
      const res = await axios.post(`${API}/api/project-templates`, {
        name: newTemplateName,
        description: newTemplateDesc,
        phases: [{
          id: `phase-${Date.now()}`,
          phase_number: FIRST_PHASE_NUMBER,
          name: 'Phase 1',
          description: '',
          steps: [],
        }],
      });
      toast.success('Template created');
      setShowNewDialog(false);
      setNewTemplateName('');
      setNewTemplateDesc('');
      await fetchTemplates();
      loadTemplate(res.data.id);
    } catch (error) {
      toast.error('Failed to create template');
    }
  };

  const handleDuplicateTemplate = async (templateId) => {
    try {
      const res = await axios.post(`${API}/api/project-templates/${templateId}/duplicate`);
      toast.success(`Duplicated as "${res.data.name}"`);
      await fetchTemplates();
      loadTemplate(res.data.id);
    } catch (error) {
      toast.error('Failed to duplicate template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (!template?.is_custom) {
      toast.error('Built-in templates cannot be deleted');
      return;
    }
    try {
      await axios.delete(`${API}/api/project-templates/${templateId}`);
      toast.success('Template deleted');
      if (selectedTemplate?.id === templateId) {
        setSelectedTemplate(null);
        setPhases([]);
      }
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  // Dark mode classes
  const bgClass = darkMode ? 'bg-black' : 'bg-[#F8F7F5]';
  const headerClass = darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : 'bg-white border-border';
  const cardClass = darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : 'bg-white border-none';
  const textClass = darkMode ? 'text-[#E5E2DD]' : '';

  const isPlatformDefault = selectedTemplate?.source === 'platform_default';

  return (
    <div className={`min-h-screen ${bgClass}`}>
      {/* Header */}
      <header className={`${headerClass} border-b sticky top-0 z-50`}>
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Breadcrumb Navigation */}
              <nav className="flex items-center gap-2 text-sm" data-testid="breadcrumb-nav">
                <button
                  onClick={() => navigate('/dashboard')}
                  className={`flex items-center gap-1 transition-colors ${darkMode ? 'text-[#8A8A8A] hover:text-[#E08B6F]' : 'text-muted-foreground hover:text-[#BC5D41]'}`}
                  data-testid="breadcrumb-my-desk"
                >
                  <Home className="w-4 h-4" />
                  My Desk
                </button>
                <ChevronRight className="w-4 h-4 text-gray-400" />
                <button
                  onClick={() => navigate('/admin')}
                  className={`transition-colors ${darkMode ? 'text-[#8A8A8A] hover:text-[#E08B6F]' : 'text-muted-foreground hover:text-[#BC5D41]'}`}
                  data-testid="breadcrumb-admin"
                >
                  Admin
                </button>
                <ChevronRight className="w-4 h-4 text-gray-400" />
                <span className={`font-medium ${darkMode ? 'text-[#E5E2DD]' : 'text-[#1A1918]'}`}>Phase Templates</span>
              </nav>
              <div className={`h-6 w-px ${darkMode ? 'bg-[#2A2A2A]' : 'bg-border'}`} />
              <div>
                <h1 className={`text-xl font-heading ${textClass}`}>
                  Phase Template Editor
                </h1>
                <span className="flex items-baseline gap-1">
                  <BrandLockup onDark={darkMode} testId="phase-template-brand" />
                </span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {hasChanges && (
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                  Unsaved Changes
                </Badge>
              )}
              <Button
                onClick={handleSaveTemplate}
                disabled={!selectedTemplate || saving || !hasChanges || isPlatformDefault}
                className="bg-[#70170F] hover:bg-[#70170F]/90 text-white"
                data-testid="save-template-btn"
              >
                {saving ? (
                  <>Saving...</>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Template
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-6 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Template Selector Sidebar */}
          <div className="col-span-3">
            <Card className={cardClass}>
              <CardHeader>
                <CardTitle className={`text-sm ${textClass}`}>Project Templates</CardTitle>
                <CardDescription>Select a template to edit</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full mb-2" 
                  onClick={() => setShowNewDialog(true)}
                  data-testid="create-template-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Template
                </Button>
                {loading ? (
                  <div className="text-center py-4 text-muted-foreground">Loading...</div>
                ) : (
                  templates.map(template => (
                    <div
                      key={template.id}
                      className={`relative group rounded-lg border transition-colors ${
                        selectedTemplate?.id === template.id
                          ? 'border-[#70170F] bg-[#70170F]/10'
                          : darkMode
                          ? 'border-gray-700 hover:bg-gray-800'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <button
                        onClick={() => loadTemplate(template.id)}
                        className="w-full text-left p-3"
                        data-testid={`template-select-${template.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`font-medium text-sm ${textClass}`}>{template.name}</span>
                          {template.is_custom ? (
                            <Badge variant="outline" className="text-[9px] h-4 px-1">Custom</Badge>
                          ) : template.is_customized ? (
                            <Badge variant="outline" className="text-[9px] h-4 px-1 text-amber-600 border-amber-400">Customized</Badge>
                          ) : (
                            <Badge variant="outline" className="text-[9px] h-4 px-1 text-gray-500">Default</Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {template.phase_count || 0} phases
                        </div>
                      </button>
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6"
                          onClick={(e) => { e.stopPropagation(); handleDuplicateTemplate(template.id); }}
                          title="Duplicate"
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                        {template.is_custom && (
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6 text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(template.id); }}
                            title="Delete"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                        {!template.is_custom && template.is_customized && (
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            onClick={(e) => { e.stopPropagation(); handleResetTemplate(template.id); }}
                            title="Reset to platform default"
                            data-testid={`template-reset-${template.id}`}
                          >
                            <RotateCcw className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {selectedTemplate && (
              <Alert className="mt-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  Changes here affect all <strong>new</strong> projects using this template. Existing projects are not affected.
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Phase Editor */}
          <div className="col-span-9">
            {!selectedTemplate ? (
              <Card className={cardClass}>
                <CardContent className="py-12 text-center">
                  <Layers className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <h3 className={`text-lg font-medium mb-2 ${textClass}`}>Select a Template</h3>
                  <p className="text-muted-foreground">
                    Choose a project template from the left to edit its phases and steps
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {isPlatformDefault && (
                  <Alert className={`border-amber-300 ${darkMode ? 'bg-amber-950/20' : 'bg-amber-50'}`} data-testid="template-platform-default-banner">
                    <Lock className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="flex items-center justify-between gap-4">
                      <span className="text-sm">
                        <strong>Platform default template — read-only.</strong>{' '}
                        Customize to create your company&apos;s own editable copy. The shared default stays untouched for everyone else.
                      </span>
                      <Button
                        size="sm"
                        onClick={handleCustomizeTemplate}
                        disabled={saving}
                        className="bg-[#BC5D41] hover:bg-[#BC5D41]/90 text-white flex-shrink-0"
                        data-testid="template-customize-btn"
                      >
                        {saving ? 'Creating copy...' : 'Customize template'}
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className={`text-lg font-semibold ${textClass}`}>{selectedTemplate.name}</h2>
                    <p className="text-sm text-muted-foreground">
                      {isPlatformDefault
                        ? 'Read-only platform default — customize to edit phases and steps.'
                        : 'Drag phases to reorder. Click on a phase to expand and edit steps.'}
                    </p>
                  </div>
                  <Button onClick={handleAddPhase} variant="outline" disabled={isPlatformDefault} data-testid="add-phase-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Phase
                  </Button>
                </div>

                <div className={isPlatformDefault ? 'pointer-events-none select-none opacity-70' : ''} data-testid="template-phases-area">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handlePhasesReorder}
                >
                  <SortableContext
                    items={phases.map(p => p.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-4">
                      {phases.map((phase, idx) => (
                        <SortablePhase
                          key={phase.id}
                          phase={{ ...phase, phase_number: idx + FIRST_PHASE_NUMBER }}
                          onUpdate={handlePhaseUpdate}
                          onDelete={handlePhaseDelete}
                          onStepsReorder={handleStepsReorder}
                          onMoveStep={handleMoveStep}
                          darkMode={darkMode}
                          allPhases={phases}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
                </div>

                {phases.length === 0 && (
                  <Card className={cardClass}>
                    <CardContent className="py-12 text-center">
                      <p className="text-muted-foreground mb-4">No phases yet</p>
                      <Button onClick={handleAddPhase}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add First Phase
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* New Template Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Create New Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Template Name</Label>
              <Input
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                placeholder="e.g. Basement Renovation"
                className="mt-1"
                data-testid="new-template-name"
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                value={newTemplateDesc}
                onChange={(e) => setNewTemplateDesc(e.target.value)}
                placeholder="What type of projects is this for?"
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTemplate} className="bg-[#70170F] hover:bg-[#70170F]/90 text-white" data-testid="create-template-confirm">
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
